import React, { useCallback, useEffect, useState } from 'react';
import { Tab } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getGallery } from '../lib/api';
import PageHeader from '../components/common/PageHeader';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const ImageViewer = ({ image, images, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(images.findIndex((img) => img.id === image.id));

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, images.length]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [handleNext, handlePrevious, onClose],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const currentImage = images[currentIndex];

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center" onClick={onClose}>
      <div className="relative w-full h-full flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        <button
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className={`absolute left-4 z-50 p-2 rounded-full ${
            currentIndex === 0 ? 'bg-gray-600 cursor-not-allowed' : 'bg-black/50 hover:bg-black/70'
          } text-white transition-colors`}
        >
          <ChevronLeftIcon className="w-6 h-6" />
        </button>

        <div className="relative max-h-[90vh] max-w-[90vw]">
          <img
            src={currentImage.image_url}
            alt={currentImage.title}
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
            <div className="max-w-3xl">
              <h3 className="text-white text-xl font-semibold">{currentImage.title}</h3>
              <p className="text-gray-300 text-sm mt-1">
                {currentImage.type === 'product' ? 'Ürün' : 'Proje'}
              </p>
              {currentImage.description && <p className="text-gray-200 mt-2">{currentImage.description}</p>}
            </div>
          </div>
        </div>

        <button
          onClick={handleNext}
          disabled={currentIndex === images.length - 1}
          className={`absolute right-4 z-50 p-2 rounded-full ${
            currentIndex === images.length - 1 ? 'bg-gray-600 cursor-not-allowed' : 'bg-black/50 hover:bg-black/70'
          } text-white transition-colors`}
        >
          <ChevronRightIcon className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

const Gallery = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedTab, setSelectedTab] = useState('all');

  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        const data = await getGallery({ active: true });
        setGallery(data || []);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  const categories = {
    all: gallery,
    products: gallery.filter((item) => item.type === 'product'),
    projects: gallery.filter((item) => item.type === 'project'),
  };

  const getImagesForViewer = () => {
    if (selectedTab === 'all') {
      return gallery;
    }

    return gallery.filter((item) => item.type === (selectedTab === 'products' ? 'product' : 'project'));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#dbc582]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Galeri" backgroundImage="/images/hero-bg.jpg" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mt-12">
          <Tab.Group onChange={(index) => setSelectedTab(Object.keys(categories)[index])}>
            <div>
              <Tab.List className="flex justify-center space-x-4">
                {Object.keys(categories).map((category) => (
                  <Tab
                    key={category}
                    className={({ selected }) =>
                      classNames(
                        'px-6 py-2 text-sm font-medium rounded-full transition-all duration-200',
                        selected
                          ? 'bg-[#dbc582] text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                      )
                    }
                  >
                    {category === 'all' ? 'Tümü' : category === 'products' ? 'Ürünler' : 'Projeler'}
                  </Tab>
                ))}
              </Tab.List>
            </div>
            <Tab.Panels className="mt-8">
              {Object.values(categories).map((items, idx) => (
                <Tab.Panel key={idx} className="grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="group relative cursor-pointer"
                      onClick={() => setSelectedImage(item)}
                    >
                      <div className="aspect-w-4 aspect-h-3 bg-gray-200 rounded-lg overflow-hidden">
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="object-cover object-center group-hover:opacity-75 transition-opacity duration-300"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300" />
                      </div>
                    </div>
                  ))}
                </Tab.Panel>
              ))}
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>

      {selectedImage && (
        <ImageViewer image={selectedImage} images={getImagesForViewer()} onClose={() => setSelectedImage(null)} />
      )}
    </div>
  );
};

export default Gallery;
